import type { ComponentProps } from "react"

/**
 * Plain-element stand-ins for framer-motion's `motion.div` / `motion.button`.
 *
 * The coverage checker was authored against framer-motion. Mounting a
 * `motion.*` component via a client state change (revealing the "Other"
 * freetext field, the add-on running total, or the result panel) lazy-loads
 * framer's animation features the first time — that dynamic import suspends
 * the subtree and, with no local Suspense boundary, the bailout walks up to
 * the root layout's Suspense and collapses the whole page to its fallback.
 *
 * The entrance fades are non-essential polish (the important step reveals
 * already animate via CSS in `coverage-form.css`), so we render plain
 * elements and drop the animation-only props. This keeps the form a clean,
 * dependency-light fit for the marketing-site rendering model.
 */
const ANIMATION_PROPS = new Set([
  "initial",
  "animate",
  "exit",
  "transition",
  "whileInView",
  "whileHover",
  "whileTap",
  "whileFocus",
  "viewport",
  "layout",
  "variants",
  "custom",
])

function stripAnimationProps(
  props: Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const key in props) {
    if (!ANIMATION_PROPS.has(key)) out[key] = props[key]
  }
  return out
}

// Accept the framer-motion animation props the call sites still pass (initial,
// animate, transition, …) via the index signature, then drop them before
// spreading onto the real element.
type DivMotionProps = ComponentProps<"div"> & Record<string, unknown>
type ButtonMotionProps = ComponentProps<"button"> & Record<string, unknown>

export const motion = {
  div: (props: DivMotionProps) => (
    <div {...(stripAnimationProps(props) as ComponentProps<"div">)} />
  ),
  button: (props: ButtonMotionProps) => (
    <button {...(stripAnimationProps(props) as ComponentProps<"button">)} />
  ),
}
